import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { LoadingScreen } from "@/components/LoadingScreen";
import type { Capability } from "@/services/auth/permissions";

/**
 * Gate on authentication and optional capability.
 * GraphQL remains the authoritative data/authorization boundary.
 *
 * `requireAuth` (default true) keeps private routes behind login.
 * Public consultation routes set `requireAuth={false}` and rely on
 * capability checks against PUBLIC caps for anonymous visitors.
 */
export function ProtectedRoute({
  children,
  requireCapability,
  requireAuth = true,
}: {
  children: ReactNode;
  requireCapability?: Capability;
  requireAuth?: boolean;
}) {
  const auth = useAuth();
  const permissions = usePermissions();
  const location = useLocation();

  if (auth.isLoading) {
    return <LoadingScreen />;
  }

  if (requireAuth && !auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requireCapability && !permissions.can(requireCapability)) {
    return (
      <Navigate
        to="/forbidden"
        replace
        state={{ from: location.pathname, capability: requireCapability }}
      />
    );
  }

  return children;
}
